import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Wrench, Plus, Trash2, Loader2 } from 'lucide-react';
import type { Asset } from '@/types/asset';
import type { Equipment } from '@/types/settings';
import { EquipmentSelectionDialog } from '@/components/equipment/equipment-selection-dialog';
import { formatCurrency } from '@/lib/format-currency';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { dataService } from '@/services/data-service';
import { useToast } from '@/hooks/use-toast';

/**
 * Merges updated equipment into existing equipment list
 */
function mergeUpdatedEquipment(
  existingEquipment: Equipment[],
  updates: Equipment[]
): Equipment[] {
  return existingEquipment.map(eq => {
    const updated = updates.find(u => u.id === eq.id);
    return updated ?? eq;
  });
}

interface AssetDetailEquipmentProps {
  readonly asset: Asset;
  readonly onAssetUpdate: (asset: Asset) => void;
  readonly isEditMode?: boolean;
}

export function AssetDetailEquipment({ asset, isEditMode }: Readonly<AssetDetailEquipmentProps>) {
  const [linkedEquipment, setLinkedEquipment] = useState<Equipment[]>([]);
  const [allEquipment, setAllEquipment] = useState<Equipment[]>([]);
  const [showSelectionDialog, setShowSelectionDialog] = useState(false);
  const [equipmentToRemove, setEquipmentToRemove] = useState<Equipment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  // Load all equipment and filter to find those linked to this asset
  useEffect(() => {
    const loadEquipment = async () => {
      setIsLoading(true);
      try {
        const equipment = await dataService.getEquipment();
        setAllEquipment(equipment);
        // Filter equipment that is linked to this asset
        const linked = equipment.filter((eq: Equipment) => eq.linkedAssetId === asset.id);
        setLinkedEquipment(linked);
      } catch (error) {
        console.error('Failed to load equipment:', error);
        toast({
          title: 'Error',
          description: 'Failed to load equipment data',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadEquipment();
  }, [asset.id, toast]);

  const handleAddEquipment = async (selectedEquipment: Equipment[]) => {
    setShowSelectionDialog(false);
    setIsSaving(true);
    
    try {
      // Update each selected equipment's linkedAssetId to this asset
      const updatePromises = selectedEquipment.map(eq =>
        dataService.updateEquipment(eq.id, { linkedAssetId: asset.id })
      );
      
      await Promise.all(updatePromises);
      
      // Update local state with the newly linked equipment
      const updatedEquipment = selectedEquipment.map(eq => ({
        ...eq,
        linkedAssetId: asset.id,
      }));
      setLinkedEquipment([...linkedEquipment, ...updatedEquipment]);
      
      // Update allEquipment to reflect the changes
      setAllEquipment(prev => mergeUpdatedEquipment(prev, updatedEquipment));
      
      toast({
        title: 'Success',
        description: `${selectedEquipment.length} equipment item(s) linked to this asset`,
      });
    } catch (error) {
      console.error('Failed to link equipment:', error);
      toast({
        title: 'Error',
        description: 'Failed to link equipment to this asset',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveEquipment = (equipment: Equipment) => {
    setEquipmentToRemove(equipment);
  };

  const confirmRemoveEquipment = async () => {
    if (!equipmentToRemove) return;
    
    setIsSaving(true);
    try {
      // Clear the linkedAssetId on the equipment record
      await dataService.updateEquipment(equipmentToRemove.id, { linkedAssetId: undefined });
      
      // Update local state
      setLinkedEquipment(linkedEquipment.filter(eq => eq.id !== equipmentToRemove.id));
      
      // Update allEquipment to reflect the change
      setAllEquipment(prev => prev.map(eq => 
        eq.id === equipmentToRemove.id ? { ...eq, linkedAssetId: undefined } : eq
      ));
      
      toast({
        title: 'Success',
        description: `Equipment "${equipmentToRemove.code}" unlinked from this asset`,
      });
    } catch (error) {
      console.error('Failed to unlink equipment:', error);
      toast({
        title: 'Error',
        description: 'Failed to unlink equipment from this asset',
        variant: 'destructive',
      });
    } finally {
      setEquipmentToRemove(null);
      setIsSaving(false);
    }
  };

  // Exclude equipment that is already linked to this asset or to other assets
  const excludedEquipmentIds = useMemo(() => {
    // Exclude equipment already linked to this asset
    const linkedIds = linkedEquipment.map(eq => eq.id);
    // Also exclude equipment linked to other assets
    const linkedToOtherAssets = allEquipment
      .filter(eq => eq.linkedAssetId && eq.linkedAssetId !== asset.id)
      .map(eq => eq.id);
    return [...linkedIds, ...linkedToOtherAssets];
  }, [linkedEquipment, allEquipment, asset.id]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card className="border border-border/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Linked Equipment
            </CardTitle>
            <CardDescription className="text-xs">
              Equipment items associated with this asset
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading equipment...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border border-border/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Linked Equipment
              </CardTitle>
              <CardDescription className="text-xs">
                Equipment items associated with this asset
              </CardDescription>
            </div>
            {isEditMode && (
              <Button 
                onClick={() => setShowSelectionDialog(true)} 
                size="sm"
                disabled={isSaving}
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Add Equipment
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {linkedEquipment.length === 0 ? (
            <div className="text-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <Wrench className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium">No equipment linked</p>
                  <p className="text-xs text-muted-foreground">
                    {isEditMode 
                      ? 'Click "Add Equipment" to link equipment items to this asset'
                      : 'No equipment has been linked to this asset'}
                  </p>
                </div>
                {isEditMode && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowSelectionDialog(true)}
                    className="mt-2"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Add Equipment
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Acquired Date</TableHead>
                    <TableHead className="text-right">Acquisition Value</TableHead>
                    {isEditMode && <TableHead className="w-[80px]"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody className="bg-white dark:bg-background">
                  {linkedEquipment.map((equipment) => (
                    <TableRow key={equipment.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Wrench className="h-4 w-4 text-muted-foreground" />
                          {equipment.code}
                        </div>
                      </TableCell>
                      <TableCell>{equipment.description ?? '-'}</TableCell>
                      <TableCell>
                        {equipment.acquiredDate 
                          ? new Date(equipment.acquiredDate).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(equipment.acquisitionValue)}
                      </TableCell>
                      {isEditMode && (
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveEquipment(equipment)}
                            className="h-8 w-8"
                            disabled={isSaving}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Equipment Selection Dialog */}
      <EquipmentSelectionDialog
        open={showSelectionDialog}
        onOpenChange={setShowSelectionDialog}
        onConfirm={handleAddEquipment}
        excludeEquipmentIds={excludedEquipmentIds}
      />

      {/* Remove Confirmation Modal */}
      <ConfirmationModal
        open={!!equipmentToRemove}
        onOpenChange={(open) => !open && setEquipmentToRemove(null)}
        onConfirm={confirmRemoveEquipment}
        title="Remove Equipment Link"
        description={`Are you sure you want to remove "${equipmentToRemove?.code}" from this asset? This will not delete the equipment record.`}
        confirmText="Remove"
        type="danger"
      />
    </div>
  );
}
